// client/src/services/lexistarPlanBoard.js
//
// LexiStar daily plan board, route history statistics and mid-trip re-plan API client
// (ST-00188, M3-A3; contracts docs/lexistar/contracts/m3-contracts.md §7.3; LS-03
// FR-TRIP-015/016/019; LS-04 §4.2; LS-09 §5.2). Server:
// server/modules/lexistar/planboard (+ the re-plan route in server/modules/lexistar/trips),
// mounted at /api/lexistar.
//
// Thin wrapper over the shared axios instance in `services/api`; every helper resolves to
// the response BODY:
//
//   getPlanBoard   { date, day: {from, to, timezone}, generated_at, filters,
//                    routes: [{route_id, code, name, status, route_version, lane: {…},
//                              geometry, geometry_estimated, corridor_m,
//                              state: 'on_plan'|'delayed'|'deviating'|'not_started', all_done,
//                              counts: {planned, en_route, done, not_started, aborted},
//                              tonnes: {planned, actual}, open_deviations, delayed_trips,
//                              advisories: [{id, reason, valid_from, valid_to, extra_km,
//                                            alt_corridor, state}],
//                              trucks: [{asset_key, registration, trip_id, reference,
//                                        progress_pct, lat, lon, heading, speed_kmh, ts,
//                                        stage, motion, context, delayed}],
//                              trips: [{id, reference, stage, class, asset_key, planned_start,
//                                       planned_end, eta, progress_pct, delayed, …}]}],
//                    sites: [{location_id, name, lat, lon, role, counts: {outbound, inbound,
//                             at_site, planned_tonnes_out, planned_tonnes_in}}],
//                    summary: {routes, trips, en_route, done, not_started, delayed,
//                              open_deviations, by_state} }
//                  routes with problems sort first (deviating, delayed, on plan, not started)
//   getRouteStats  { route_id, code, name, planned_km, stats: [{load_state, n, avg_km,
//                    avg_min, avg_l, window_days, computed_at, drift, drift_pct}],
//                    drift: {flag, pct, avg_km, planned_km, threshold}, computed_at, method }
//                  503 until migration 026 is applied
//   replanTrip     { trip, legs, plan_version, mode: 'before_loading'|'in_transit',
//                    replaced_leg_ids, new_legs, budget: {budget_adjusted_l,
//                    budget_adjusted_l_before, adjusted_km, …, formula}, route (the generated
//                    route when one was created) | null, position, flags, command, outbox }

import api from './api';

const LX = '/lexistar';

const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
const body = (res) => res.data;

/** { date?: 'YYYY-MM-DD' (SAST day, default today), customer?, route? (id or code), material? } */
export const getPlanBoard = (params = {}) =>
  api.get(`${LX}/plan-board`, { params: clean(params) }).then(body);

/** Route history statistics of a route (both load states + drift). */
export const getRouteStats = (routeId) => api.get(`${LX}/routes/${routeId}/stats`).then(body);

/**
 * Mid-trip re-plan (lexistar.trips.dispatch): { to_location_id? | route_id?,
 * via_location_id?, reason_code, reason, version? } — route_id and via_location_id are
 * mutually exclusive; at least one of to_location_id / route_id / via_location_id.
 */
export const replanTrip = (tripId, data) =>
  api.post(`${LX}/trips/${tripId}:replan`, clean(data)).then(body);
